import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Group, Badge, Text, Button, SimpleGrid } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Select, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, type logs } from '@repletoryreach/sdk-api';
import { useLogQuery, useLogStats } from '../../data/useLogStats';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { CopyableId } from '../../components/CopyableId';
import { StatCard } from '../../components/StatCard';
import { TableFooter } from '../../components/TableFooter';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatDateTime, formatInteger, formatRelative } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { useT } from '../../i18n/I18nProvider';
import { FAMILIES, LEVELS, buildColumns, levelColor, levelLabel, primarySummary } from './columns';
import { LogDetailDrawer } from './components/LogDetailDrawer';

const MUTED = 'var(--rr-color-text-muted)';
const LIMITS = [100, 250, 500];
const STATUS_CLASSES = ['2xx', '3xx', '4xx', '5xx'] as const;

/**
 * The windows the endpoint's `since` parameter is worth offering. `all` sends
 * no bound at all, which is what the page did before it had a window: fine on
 * a dev stack, misleading on a busy one, so it is a choice rather than the
 * default.
 */
type RangeKey = '15m' | '1h' | '24h' | '7d' | 'all';
const RANGE_MS: Record<Exclude<RangeKey, 'all'>, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000
};

const familyIcon = (d: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{d}</svg>
);
const ApiIcon = familyIcon(<><path d="M9 7L4.5 12 9 17" /><path d="M15 7l4.5 5-4.5 5" /></>);
const QueryIcon = familyIcon(<><ellipse cx="12" cy="6" rx="7" ry="2.8" /><path d="M5 6v12c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8V6" /><path d="M5 12c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8" /></>);
const AppIcon = familyIcon(<><rect x="3.5" y="4.5" width="17" height="15" rx="2.5" /><path d="M3.5 9h17M7.5 13h6" /></>);
const LifecycleIcon = familyIcon(<><path d="M20 12a8 8 0 1 1-2.4-5.7" /><path d="M20 4v4.5h-4.5" /></>);
const FrontendIcon = familyIcon(<><rect x="3" y="4.5" width="18" height="12" rx="2" /><path d="M8.5 20h7M12 16.5V20" /></>);
const TotalIcon = familyIcon(<><path d="M4.5 6.5h15M4.5 12h15M4.5 17.5h9" /></>);

const RefreshIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 11a8 8 0 1 0-2.3 6.3" /><path d="M20 5v6h-6" />
  </svg>
);
const DownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 4v10M8 10.5l4 4 4-4" /><path d="M4.5 19h15" />
  </svg>
);

export default function LogsPage() {
  const t = useT();
  const nav = useNavigate();
  const statsQ = useLogStats();

  // `family` doubles as the KPI value: flipping it re-queries the log family
  // server-side. Every filter below is a real server parameter, so the table
  // reflects the whole family rather than a locally sieved page of it.
  // Opens on `app`, not `api`. The App family is the one that carries service
  // warnings and errors, which is what somebody opening this page is nearly
  // always looking for. The API family stays in the row but is still empty on
  // every environment: nothing calls `UseRepletoryRequestLogging()`, so
  // `api_logs` takes no traffic, and landing an operator on an empty table
  // reads as "there is nothing wrong" rather than "this family is unwired".
  const [family, setFamily] = usePersistedState<logs.LogFamily>('admin.logs.family', 'app');
  const [view, setView] = usePersistedState<ViewMode>('admin.logs.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.logs.pageSize', 25);
  const [limit, setLimit] = usePersistedState<number>('admin.logs.limit', 500);
  const [range, setRange] = usePersistedState<RangeKey>('admin.logs.range', '24h');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  // Which row the detail panel is showing, as a position in the visible page.
  const [selected, setSelected] = useState<number | null>(null);

  // Recomputed on every range change rather than every render, so the query key
  // does not churn on a timer and refetch the same window continuously.
  const since = useMemo(
    () => (range === 'all' ? undefined : new Date(Date.now() - RANGE_MS[range]).toISOString()),
    [range]
  );

  const queryOpts: logs.LogQueryOptions = useMemo(
    () => ({
      family,
      ...(filters.level && { level: filters.level }),
      ...(filters.service && { service: filters.service }),
      ...(filters.env && { env: filters.env }),
      // `status_class` is an API-family filter server-side, so sending it for
      // another family would be a control that silently does nothing.
      ...(family === 'api' && filters.status_class
        ? { status_class: filters.status_class as (typeof STATUS_CLASSES)[number] }
        : {}),
      ...(since && { since }),
      ...(query.trim() && { q: query.trim() }),
      limit
    }),
    [family, filters.level, filters.service, filters.env, filters.status_class, since, query, limit]
  );

  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useLogQuery(queryOpts);
  const rows = useMemo(() => (data?.data ?? []) as logs.LogRow[], [data]);
  const stats = statsQ.data?.data;
  const denied = isApiError(error) && error.status === 403;
  // `meta.count` reaching the limit means the server had more to give, so the
  // page is a window on the family and not the family itself.
  const capped = (data?.meta?.count ?? 0) >= limit;

  const kpiItems: KpiFilterItem[] = [
    { id: 'api', label: t('logs.kpi.api'), icon: ApiIcon, value: stats?.api_logs ?? 0, tone: 'primary' },
    { id: 'app', label: t('logs.kpi.app'), icon: AppIcon, value: stats?.app_logs ?? 0, tone: 'healthy' },
    { id: 'frontend', label: t('logs.kpi.frontend'), icon: FrontendIcon, value: stats?.frontend_logs ?? 0, tone: 'warning' },
    { id: 'query', label: t('logs.kpi.query'), icon: QueryIcon, value: stats?.query_logs ?? 0, tone: 'pending' },
    { id: 'lifecycle', label: t('logs.kpi.lifecycle'), icon: LifecycleIcon, value: stats?.lifecycle_logs ?? 0, tone: 'neutral' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const services = Array.from(new Set(rows.map((r) => String(r.service_name ?? '')).filter(Boolean))).sort();
    const envs = Array.from(new Set(rows.map((r) => String(r.environment ?? '')).filter(Boolean))).sort();
    const fields: FilterField[] = [
      { key: 'level', label: t('logs.filters.level'), placeholder: t('logs.filters.levelPlaceholder'), options: LEVELS.map((l) => ({ value: l.value, label: t(l.labelKey) })) },
      { key: 'service', label: t('logs.filters.service'), placeholder: t('logs.filters.servicePlaceholder'), options: services.map((s) => ({ value: s, label: s })) },
      { key: 'env', label: t('logs.filters.env'), placeholder: t('logs.filters.envPlaceholder'), options: envs.map((e) => ({ value: e, label: e })) }
    ];
    if (family === 'api') {
      fields.push({
        key: 'status_class',
        label: t('logs.filters.statusClass'),
        placeholder: t('logs.filters.anyStatusClass'),
        options: STATUS_CLASSES.map((s) => ({ value: s, label: s }))
      });
    }
    return fields;
  }, [rows, family, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || drawerActive > 0 || range !== '24h';
  function clearAllFilters() { setQuery(''); setFilters({}); setRange('24h'); setPage(1); }

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= rows.length) setPage(1);
  }, [rows.length, page, pageSize]);

  const paged = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  );

  const selectedRow = selected !== null ? paged[selected] ?? null : null;

  /*
   * A figure the stats query did not return is not zero rows: it is a figure
   * the console does not have. `formatInteger(x ?? 0)` printed a confident
   * "0" for both, which on a logs page reads as "nothing is being written".
   */
  const statsCards: { label: string; value: number | null | undefined; hint?: string; icon: React.ReactNode; tone: 'primary' | 'info' | 'success' | 'neutral' | 'warning' }[] = [
    { label: t('logs.stats.all'), value: stats?.total, hint: t('logs.stats.rowsHint'), icon: TotalIcon, tone: 'primary' },
    { label: t('logs.stats.apiLogs'), value: stats?.api_logs, icon: ApiIcon, tone: 'primary' },
    { label: t('logs.stats.appLogs'), value: stats?.app_logs, icon: AppIcon, tone: 'success' },
    { label: t('logs.stats.frontend'), value: stats?.frontend_logs, icon: FrontendIcon, tone: 'warning' },
    { label: t('logs.stats.queryLogs'), value: stats?.query_logs, icon: QueryIcon, tone: 'info' },
    { label: t('logs.stats.lifecycle'), value: stats?.lifecycle_logs, icon: LifecycleIcon, tone: 'neutral' }
  ];

  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (range !== '24h') pills.push({ id: 'range', field: t('audit.range.label'), value: t(`logs.range.${range}`) });
  if (filters.level) {
    pills.push({ id: 'level', field: t('logs.filters.level'), value: t(LEVELS.find((l) => l.value === filters.level)?.labelKey ?? '') });
  }
  if (filters.service) pills.push({ id: 'service', field: t('logs.filters.service'), value: filters.service });
  if (filters.env) pills.push({ id: 'env', field: t('logs.filters.env'), value: filters.env });
  if (filters.status_class) pills.push({ id: 'status_class', field: t('logs.filters.statusClass'), value: filters.status_class });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') { setQuery(''); return; }
    if (id === 'range') { setRange('24h'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  /**
   * The rows currently loaded, as the JSON they arrived as. An operator
   * attaching evidence to an incident wants the documents, not a rendering of
   * them, and the export says which query produced it.
   */
  function exportJson() {
    const body = JSON.stringify({ query: queryOpts, exported_at: new Date().toISOString(), rows }, null, 2);
    downloadText(body, `logs-${family}-${Date.now()}.json`, 'application/json');
    notifySuccess(t('logs.export.done', { count: formatInteger(rows.length) }), t('logs.export.title'));
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('logs.title')}
        description={t('logs.description')}
        actions={
          <Group gap="sm" align="center">
            {dataUpdatedAt > 0 && (
              <Text role="caption" c={MUTED}>
                {t('logs.lastUpdated', { when: formatRelative(new Date(dataUpdatedAt).toISOString()) })}
              </Text>
            )}
            <Button size="sm" variant="default" leftSection={RefreshIcon} onClick={() => refetch()} loading={isFetching}>
              {t('action.refresh')}
            </Button>
            <Button size="sm" variant="default" leftSection={DownloadIcon} onClick={exportJson} disabled={rows.length === 0}>
              {t('logs.export.action')}
            </Button>
            <Button size="sm" variant="subtle" onClick={() => nav('/logs/query')}>{t('logs.openQueryBuilder')}</Button>
          </Group>
        }
      />

      {denied && (
        <Alert color="warning" title={t('logs.denied.title')}>{t('logs.denied.body')}</Alert>
      )}

      {statsQ.error && (
        <Alert color="warning">
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>{t('logs.statsLoadError')}</span>
            <Button size="xs" variant="outline" onClick={() => statsQ.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      {/* The tiles count the whole database and the table shows one window of
          one family, so the two never agree; the caption says so rather than
          leaving the operator to discover it. A failed stats query renders no
          number at all, because a zero here reads as "no logs". */}
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
          {statsCards.map((card) => (
            <StatCard
              key={String(card.label)}
              icon={card.icon}
              label={card.label}
              value={statsQ.error || card.value == null ? t('common.notAvailable') : formatInteger(card.value)}
              hint={card.hint}
              tone={card.tone}
              loading={statsQ.isLoading}
              withRule
            />
          ))}
        </SimpleGrid>
        <Text role="caption" c={MUTED}>{t('logs.stats.scopeCaption')}</Text>
      </Stack>

      <TableShell
        title={t('logs.tableTitle', { family: t(FAMILIES.find((f) => f.value === family)?.labelKey ?? '') })}
        description={capped ? t('logs.capReached', { limit }) : t('logs.tableDescription')}
        totalLabel={t('logs.totalLabel', { shown: rows.length, total: data?.meta?.count ?? rows.length })}
        kpis={
          <KpiFilterRow
            items={kpiItems}
            value={family}
            onChange={(next) => { if (next) { setFamily(next as logs.LogFamily); setPage(1); } }}
            filteringLabel={t('filters.filtering')}
          />
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('logs.searchPlaceholder')}
        filters={
          <>
            {/* The window is a server-side bound, so it belongs with the other
                controls that narrow the query rather than in the page header. */}
            <SegmentedControl
              size="xs"
              aria-label={t('audit.range.label')}
              value={range}
              onChange={(v) => { setRange(v as RangeKey); setPage(1); }}
              data={(['15m', '1h', '24h', '7d', 'all'] as RangeKey[]).map((r) => ({ value: r, label: t(`logs.range.${r}`) }))}
            />
            <FilterDrawer
              fields={filterFields}
              values={filters}
              onApply={(next) => { setFilters(next); setPage(1); }}
            />
            <Select
              size="xs"
              aria-label={t('logs.limitLabel')}
              value={String(limit)}
              onChange={(e) => { setLimit(Number(e.currentTarget.value)); setPage(1); }}
              data={LIMITS.map((n) => ({ value: String(n), label: t('logs.limitOption', { count: n }) }))}
            />
          </>
        }
        belowToolbar={<ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />}
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={!isLoading && !error && rows.length === 0}
        empty={
          <EmptyState
            title={t('logs.empty')}
            description={hasActiveFilters ? t('logs.emptyFiltered') : t('logs.emptyHint')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={error && !denied ? (
          <Alert color="error">
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <span>{t('logs.loadError')}</span>
              <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={rows.length}
            capped={capped}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        {view === 'table' && (
          <DataTable<Indexed<logs.LogRow>>
            records={paged as Indexed<logs.LogRow>[]}
            loading={isLoading}
            idAccessor={(r, i) => `${r.id ?? i}`}
            columns={buildColumns(t, family)}
            emptyState={t('logs.tableEmpty')}
            highlightOnHover
            onRowClick={(_, i) => setSelected(i)}
          />
        )}
        {view === 'list' && (
          <Stack gap="xs">
            {paged.map((r, i) => (
              <button
                type="button"
                key={`${r.id ?? i}`}
                className="rr-list-row"
                onClick={() => setSelected(i)}
                aria-label={t('logs.detail.open')}
              >
                <Group gap="md" align="center" wrap="wrap" style={{ width: '100%' }}>
                  <Badge size="xs" color={levelColor(r.level)} variant="light" style={{ flexShrink: 0 }}>
                    {levelLabel(r.level)}
                  </Badge>
                  <Stack gap={0} style={{ flex: 1, minWidth: 220 }}>
                    <Text role="subtitle" fw={500} truncate>
                      {primarySummary(t, family, r)}
                    </Text>
                    <Group gap="xs" align="baseline" wrap="wrap">
                      <Text role="caption" c={MUTED}>
                        <code>{String(r.service_name ?? t('common.none'))}</code> · {String(r.environment ?? t('common.none'))} ·{' '}
                        <time dateTime={r.occurred_at} title={formatDateTime(r.occurred_at)}>
                          {formatRelative(r.occurred_at)}
                        </time>
                      </Text>
                      {r.correlation_id && (
                        <CopyableId value={String(r.correlation_id)} label={t('logs.copyCorrelation')} />
                      )}
                    </Group>
                  </Stack>
                </Group>
              </button>
            ))}
          </Stack>
        )}
        {view === 'cards' && (
          <EntityCardGrid<logs.LogRow>
            records={paged}
            idAccessor={(r) => String(r.id)}
            cols={3}
            spacing="md"
            onCardClick={(r) => setSelected(paged.indexOf(r))}
            renderCard={(r) => (
              <Stack gap="sm">
                <Group justify="space-between" align="baseline">
                  <Badge size="xs" color={levelColor(r.level)} variant="light">{levelLabel(r.level)}</Badge>
                  <Text role="caption" c={MUTED}>
                    <time dateTime={r.occurred_at} title={formatDateTime(r.occurred_at)}>
                      {formatRelative(r.occurred_at)}
                    </time>
                  </Text>
                </Group>
                <Text role="bodySmall" truncate>{primarySummary(t, family, r)}</Text>
                <Group gap="xs" align="baseline" wrap="wrap">
                  <Text role="caption" c={MUTED}>
                    <code>{String(r.service_name ?? t('common.none'))}</code> · {String(r.environment ?? t('common.none'))}
                  </Text>
                  {r.correlation_id && (
                    <CopyableId value={String(r.correlation_id)} label={t('logs.copyCorrelation')} />
                  )}
                </Group>
              </Stack>
            )}
          />
        )}
      </TableShell>

      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text role="caption" c={MUTED}>
          {dataUpdatedAt > 0
            ? t('logs.footer.fresh', { when: formatDateTime(new Date(dataUpdatedAt).toISOString()) })
            : t('logs.footer.notLoaded')}
        </Text>
        <Text role="caption" c={MUTED}>{t('logs.footer.timezone')}</Text>
      </Group>

      <LogDetailDrawer
        row={selectedRow}
        family={family}
        onClose={() => setSelected(null)}
        onPrevious={selected !== null && selected > 0 ? () => setSelected(selected - 1) : undefined}
        onNext={selected !== null && selected < paged.length - 1 ? () => setSelected(selected + 1) : undefined}
      />
    </Stack>
  );
}
